import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '@progress/kendo-react-buttons'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import { FaLink, FaLock, FaSearchDollar, FaSyncAlt } from 'react-icons/fa'

import { useToast } from '../../../contexts/ToastContext'
import { purchaseOrder, purchaseOrderMatch } from '../../../data/interfaces'
import {
  allocatePurchaseOrder,
  deallocatePurchaseOrder,
  getAllocatablePurchaseOrders,
  getPurchaseOrderMatchForPiece,
} from '../../../data/purchaseOrderData'

interface PiecePurchaseOrderPanelProps {
  piecesId: number
  /**
   * The job picked on the form, saved or not. When set, only that job's
   * purchase orders can be chosen; 0 means no job, and every order is offered.
   */
  jobId?: number
  lockEdit?: boolean
  /** Bumped by the parent after a save, so a changed reference is rechecked. */
  refreshToken?: number
}

const formatMoney = (value: number | null | undefined) =>
  typeof value === 'number'
    ? `£${value.toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : '£0.00'

const orderLabel = (order: purchaseOrder) =>
  `${order.orderNo} - ${order.supplierName || 'No supplier'} - ${formatMoney(order.grossValue)}`

/**
 * Ties an incoming supplier invoice to a purchase order. A match on the typed
 * reference is only ever a suggestion - the user confirms it - and once the
 * piece has gone to Xero the pairing is fixed.
 */
const PiecePurchaseOrderPanel = (props: PiecePurchaseOrderPanelProps) => {
  const { showToast } = useToast()

  const [match, setMatch] = useState<purchaseOrderMatch | null>(null)
  const [options, setOptions] = useState<purchaseOrder[]>([])
  const [selected, setSelected] = useState<purchaseOrder | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [choosing, setChoosing] = useState(false)
  // The endpoints are security trimmed, so a user without a PurchaseOrders
  // permission gets a 403. Hide the panel rather than claim there's no match.
  const [unavailable, setUnavailable] = useState(false)

  const load = useCallback(async () => {
    if (!props.piecesId) return

    setLoading(true)
    try {
      const result = await getPurchaseOrderMatchForPiece(props.piecesId)
      setMatch(result)
      setUnavailable(false)
      setChoosing(false)
      setSelected(null)
    } catch (error) {
      console.error('Error loading purchase order match:', error)
      setMatch(null)
      setUnavailable(true)
    } finally {
      setLoading(false)
    }
  }, [props.piecesId])

  useEffect(() => {
    load()
  }, [load, props.refreshToken])

  // With a job picked, only its purchase orders are offered. Worked out from
  // the job as it stands on the form, so changing the job while choosing
  // narrows the list straight away.
  const jobId = props.jobId || 0
  const jobOptions = useMemo(
    () => (jobId ? options.filter((o) => o.jobId === jobId) : options),
    [options, jobId]
  )

  // A choice that belongs to another job goes when the job changes.
  useEffect(() => {
    if (selected && !jobOptions.some((o) => o.purchaseOrderId === selected.purchaseOrderId)) {
      setSelected(null)
    }
  }, [jobOptions, selected])

  const startChoosing = async () => {
    setChoosing(true)
    try {
      const available = await getAllocatablePurchaseOrders(props.piecesId)
      setOptions(available)
      // Pre-select whatever the reference matched, so confirming is one click -
      // as long as it's on the chosen job (the effect above clears it if not).
      const suggested = available.find((o) => o.purchaseOrderId === match?.purchaseOrderId)
      setSelected(suggested || null)
    } catch (error) {
      console.error('Error loading allocatable purchase orders:', error)
      showToast('Failed to load purchase orders', 'error')
    }
  }

  const allocate = async (purchaseOrderId: number) => {
    setSaving(true)
    try {
      await allocatePurchaseOrder(purchaseOrderId, props.piecesId)
      showToast('Purchase order allocated', 'success')
      await load()
    } catch (error: any) {
      console.error('Error allocating purchase order:', error)
      showToast(
        error?.response?.data?.message || 'Failed to allocate the purchase order',
        'error'
      )
    } finally {
      setSaving(false)
    }
  }

  const release = async () => {
    if (!match?.purchaseOrderId) return

    setSaving(true)
    try {
      await deallocatePurchaseOrder(match.purchaseOrderId)
      showToast('Purchase order released', 'success')
      await load()
    } catch (error: any) {
      console.error('Error releasing purchase order:', error)
      showToast(
        error?.response?.data?.message || 'Failed to release the purchase order',
        'error'
      )
    } finally {
      setSaving(false)
    }
  }

  const containerStyle: React.CSSProperties = {
    marginTop: '1.5rem',
    marginBottom: '1rem',
    padding: '1rem',
    borderRadius: '0.5rem',
    backgroundColor: 'rgb(245 249 251)',
    border: '1px solid rgb(216 229 237)',
  }

  const headingStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.5rem',
    marginBottom: '0.75rem',
    fontWeight: 600,
    color: 'rgb(36, 32, 56)',
  }

  if (unavailable) {
    return null
  }

  if (loading && !match) {
    return (
      <div style={containerStyle}>
        <div style={headingStyle}>Purchase Order</div>
        <div>Checking for a matching purchase order...</div>
      </div>
    )
  }

  return (
    <div style={containerStyle}>
      <div style={headingStyle}>
        <span>Purchase Order</span>
        <FaSyncAlt
          title='Re-check for a match'
          style={{ cursor: 'pointer', color: '#1e2a4e' }}
          onClick={load}
        />
      </div>

      {/* Allocated, and fixed because the piece has gone to Xero. */}
      {match?.isAllocated && match.allocationLocked && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#5c4600' }}>
          <FaLock />
          <span>
            Allocated to <strong>{match.orderNo}</strong> ({match.supplierName}) and sent to
            Xero. This purchase order can no longer be allocated to another invoice.
          </span>
        </div>
      )}

      {/* Allocated, still changeable. */}
      {match?.isAllocated && !match.allocationLocked && (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'rgb(94 142 23)',
            }}
          >
            <FaLink />
            <span>
              Allocated to <strong>{match.orderNo}</strong>
              {match.supplierName ? ` (${match.supplierName})` : ''}
              {match.jobRef ? ` on ${match.jobRef}` : ''}
              {typeof match.grossValue === 'number'
                ? ` - ${formatMoney(match.grossValue)}`
                : ''}
            </span>
          </div>
          {!props.lockEdit && (
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem' }}>
              <Button type='button' onClick={startChoosing} disabled={saving}>
                Change
              </Button>
              <Button type='button' themeColor='error' onClick={release} disabled={saving}>
                {saving ? 'Working...' : 'Release'}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Not allocated, but the typed reference matches a published order. */}
      {!match?.isAllocated && match?.hasMatch && (
        <div>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#b06a00' }}
          >
            <FaSearchDollar />
            <span>
              Purchase order <strong>{match.orderNo}</strong>
              {match.supplierName ? ` (${match.supplierName})` : ''} matches the reference
              "{match.piecesPurchaseOrder}" on this invoice
              {typeof match.grossValue === 'number'
                ? ` - ${formatMoney(match.grossValue)}`
                : ''}
              .
            </span>
          </div>
          {!props.lockEdit && (
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem' }}>
              <Button
                type='button'
                themeColor='primary'
                onClick={() => allocate(match.purchaseOrderId!)}
                disabled={saving}
              >
                {saving ? 'Allocating...' : `Allocate ${match.orderNo}`}
              </Button>
              <Button type='button' onClick={startChoosing} disabled={saving}>
                Choose another
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Nothing found. */}
      {!match?.isAllocated && !match?.hasMatch && (
        <div>
          <div style={{ color: '#8a8a8a' }}>
            {match?.piecesPurchaseOrder
              ? `No published purchase order matches "${match.piecesPurchaseOrder}".`
              : 'No purchase order reference on this invoice.'}
          </div>
          {!props.lockEdit && (
            <div style={{ marginTop: '0.75rem' }}>
              <Button type='button' onClick={startChoosing} disabled={saving}>
                Allocate a purchase order
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Manual picker */}
      {choosing && !props.lockEdit && (
        <div style={{ marginTop: '1rem' }}>
          <DropDownList
            data={jobOptions}
            textField='orderNo'
            dataItemKey='purchaseOrderId'
            value={selected}
            onChange={(event) => setSelected(event.value)}
            itemRender={(li, itemProps) =>
              React.cloneElement(li, li.props, orderLabel(itemProps.dataItem))
            }
            valueRender={(element, value) =>
              value ? <span>{orderLabel(value)}</span> : element
            }
            style={{ width: '100%' }}
          />
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem' }}>
            <Button type='button' onClick={() => setChoosing(false)} disabled={saving}>
              Cancel
            </Button>
            <Button
              type='button'
              themeColor='primary'
              onClick={() => selected && allocate(selected.purchaseOrderId)}
              disabled={saving || !selected}
            >
              {saving ? 'Allocating...' : 'Allocate'}
            </Button>
          </div>
          {jobOptions.length === 0 && (
            <div style={{ marginTop: '0.5rem', color: '#8a8a8a' }}>
              {jobId
                ? 'No published, unallocated purchase orders are available for the selected job.'
                : 'No published, unallocated purchase orders are available.'}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default PiecePurchaseOrderPanel
